import { SPLASH_SCREEN_ROUTING } from '@/views/business/settings/splash-screens/const.js'

type BooksyPayEntryTarget = 'activation' | 'onboarding'

type BooksyPayStore = {
  state: {
    business: {
      booksyPaySettings: unknown
    }
  }
  getters: Record<string, boolean>
  dispatch: (action: string) => Promise<unknown>
}

type BooksyPayRoute = {
  params?: Record<string, unknown>
  query?: Record<string, unknown>
}

type EntryContext = {
  store: BooksyPayStore
  route: BooksyPayRoute
}

export const BOOKSY_PAY_ENTRY_TARGETS = {
  ACTIVATION: 'activation' as BooksyPayEntryTarget,
  ONBOARDING: 'onboarding' as BooksyPayEntryTarget,
}

const ensureBooksyPaySettingsLoaded = async (store: BooksyPayStore): Promise<void> => {
  if (!store.state.business.booksyPaySettings) {
    await store.dispatch('business/loadBooksyPaySettings')
  }
}

const getOnboardingRoute = ({ route }: EntryContext): Record<string, unknown> => ({
  name: 'settings.payments.booksy-pay.onboarding',
  params: {
    businessId: route?.params?.businessId,
  },
  query: route?.query,
})

const getActivationRoute = ({ route }: EntryContext): Record<string, unknown> => ({
  ...SPLASH_SCREEN_ROUTING.ENABLE_FEATURE.BOOKSY_PAY_ACTIVATION,
  params: {
    ...SPLASH_SCREEN_ROUTING.ENABLE_FEATURE.BOOKSY_PAY_ACTIVATION?.params,
    businessId: route?.params?.businessId,
  },
  query: {
    ...SPLASH_SCREEN_ROUTING.ENABLE_FEATURE.BOOKSY_PAY_ACTIVATION?.query,
    ...route?.query,
  },
})

const getEntryRedirectRoute = ({
  targetView,
  store,
  route,
}: EntryContext & { targetView: BooksyPayEntryTarget }): Record<string, unknown> | null => {
  const isOwner = store.getters['identity/isOwner']
  const isBooksyPayAllowed = store.getters['business/isBooksyPayAllowed']
  const isBooksyPayEnabled = store.getters['business/isBooksyPayEnabled']

  if (!isOwner || !isBooksyPayAllowed) {
    return { name: 'calendar' }
  }

  if (targetView === BOOKSY_PAY_ENTRY_TARGETS.ACTIVATION && isBooksyPayEnabled) {
    return getOnboardingRoute({ store, route })
  }

  if (targetView === BOOKSY_PAY_ENTRY_TARGETS.ONBOARDING && !isBooksyPayEnabled) {
    return getActivationRoute({ store, route })
  }

  return null
}

export const resolveBooksyPayEntryRedirect = async ({
  targetView,
  store,
  route,
}: EntryContext & { targetView: BooksyPayEntryTarget }): Promise<Record<string, unknown> | null> => {
  await ensureBooksyPaySettingsLoaded(store)
  return getEntryRedirectRoute({ targetView, store, route })
}
